/* eslint-disable unicorn/prefer-spread */
/* eslint-disable unicorn/prefer-structured-clone */
//helpers

import { format, isBefore } from "date-fns";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import twitter from "twitter-text";

import { capitalize, countCharacter, isUrl } from "../../../utils/strings";
import { isDateInThePast } from "../../../utils/dates";

import { postPlatformsConfig, RULES, RulesType } from "./postPlatforms.config";
import { DATE_FORMAT } from "../../../constants/common";
import {
  FIELDS,
  GMB_TOPICS,
  PostFormFieldName,
} from "../../../constants/PostFormFields";
import { CHARACTER_LIMITS } from "../../../constants/CharacterLimits";
import {
  POST_STATES,
  POST_TYPES,
  PostStatesType,
} from "../../../constants/PostsView";
import { PLATFORMS } from "../../../constants/Platforms";
import { FACEBOOK_STORY_CONSTANTS } from "../../../constants/VideoReelConstants";
import { getMediaFileDuration, getRatio } from "../../../entities/media-files";
import {
  getTwitterLongformCapability,
  TWITTER_SHORT_LIMIT,
  TWITTER_LONGFORM_LIMIT,
} from "../../../entities/accounts/libs/is-twitter-longform-capable";
import type {
  MediaFilesErrors,
  MediaFilesWarning,
  Post,
} from "../../../features/post";
import { GMB_MIN_IMAGE_BYTES } from "../../../features/post/utils/gmb-media-validation";
import { mergeGmbPostFields } from "../../../features/post/utils/gmb-post-fields";
import {
  API_STORY_SET_MAX,
  getStoryMediaLimit,
  isAutoPublishedRegularStory,
  PHONE_STORY_CHAIN_MAX,
} from "../../../utils/posts";
import type { IgPreflightResult } from "../../../api/post";
import {
  filterAccountsByPlatformType,
  getAccountNameParts,
} from "../../../utils/accounts";
import { Platform } from "../../../features/post/format-post";
import {
  FormValidationErrors,
  FormValidationWarnings,
  FormPlatformType,
  GoogleMyBusinessPost,
  MediaUrlInfo,
  PlatformFields,
  PostMediaFile,
} from "../../../shared/types/post";
import {
  getVideoReelCriteria,
  isNineToSixteenRatio,
  shouldShowReelToggle,
} from "./utils/videoReelUtils";
import { getFileExtension } from "../../../libs/files";
import { getEffectiveUrlWithUtms, PlatformAccountLike } from "./utils/urlUtms";
import { preflightNotices } from "./utils/preflightFindings";
import { IOverlapSchedule } from "./types";
import { validateLinkedIn } from "./validations/li-validation";
import { isDocumentMedia, isVideoMedia } from "../../../utils/media";
import { countMentionsForPlatform } from "./utils/mentionCount";

function getValueForRule({
  field,
  ruleKey,
  platform,
  post,
}: {
  field: PostFormFieldName;
  ruleKey: RulesType;
  platform: Platform;
  post: Post;
}) {
  /*
   * HASHTAG_HARD_LIMIT is deliberately absent here. Meta's ceiling applies to
   * the caption alone, so combining the first comment into it would claim a
   * rejection for posts the server publishes fine.
   */
  if (platform.TYPE === "IG" && ruleKey === RULES.HASHTAG_COUNT) {
    return `${post.caption ?? ""} ${post.firstcomment ?? ""}`;
  }

  return getSafeFieldValue(post, field);
}

type LinkedUnlinkedPlatforms = {
  linkedPlatforms: Platform[];
  unlinkedPlatforms: Platform[];
};

type ValidationMediaMeta = {
  width?: number;
  height?: number;
  size?: number;
  byteSize?: number;
  format?: {
    duration?: number;
    type?: string;
    mime?: string;
  };
  isVideo?: boolean;
  isImage?: boolean;
  length?: number;
};

export let fbMinimum = 120;
const HTML_LIKE_TAG_PATTERN = /<\/?[a-z][^>]*>/i;
const DEFAULT_UTM_CONTENT_ID = "x".repeat(24);

type CaptionCountPost = {
  _id?: string;
  id?: string;
  caption?: string | null;
  redirectUrl?: string | null;
  accounts?: PlatformAccountLike[];
};

function addFieldError(errors: Errors, field: string, message: string) {
  if (!errors[field]) {
    errors[field] = { errors: [] };
  }

  if (!errors[field].errors.includes(message)) {
    errors[field].errors.push(message);
  }
}

function getLongestEffectiveRedirectUrl({
  post,
  platformType,
  source,
}: {
  post: {
    _id?: string;
    id?: string;
    redirectUrl?: string | null;
    accounts?: PlatformAccountLike[];
  };
  platformType: string;
  source: string;
}) {
  const platformAccounts = (post.accounts || []).filter(
    (account) => account.platformType === platformType
  );
  const contentId = post._id || post.id || DEFAULT_UTM_CONTENT_ID;
  const accountsToCheck =
    platformAccounts.length > 0 ? platformAccounts : [undefined];

  return accountsToCheck.reduce((longestUrl, account) => {
    const currentUrl = getEffectiveUrlWithUtms({
      account,
      redirectUrl: post.redirectUrl,
      source,
      content: String(contentId),
    });

    return currentUrl.length > longestUrl.length ? currentUrl : longestUrl;
  }, "");
}

function getPlatformEffectiveCaptionLength({
  post,
  platformType,
  source,
  redirectUrlSeparator = " ",
}: {
  post: {
    _id?: string;
    id?: string;
    caption?: string | null;
    redirectUrl?: string | null;
    accounts?: PlatformAccountLike[];
  };
  platformType: string;
  source: string;
  redirectUrlSeparator?: string;
}) {
  const caption = post.caption ?? "";
  const effectiveRedirectUrl = getLongestEffectiveRedirectUrl({
    post,
    platformType,
    source,
  });

  return `${caption}${
    effectiveRedirectUrl ? `${redirectUrlSeparator}${effectiveRedirectUrl}` : ""
  }`.length;
}

function hasHtmlLikeTag(value: unknown): value is string {
  return typeof value === "string" && HTML_LIKE_TAG_PATTERN.test(value);
}

function getHtmlLikeTagError(value: unknown): string | null {
  if (!hasHtmlLikeTag(value)) {
    return null;
  }

  const [tag] = value.match(HTML_LIKE_TAG_PATTERN) as RegExpMatchArray;
  return `HTML-like tags such as ${tag} are not supported in captions. Please remove them before saving.`;
}

function getUrlValidationMessage(url: unknown): string | null {
  if (typeof url !== "string") {
    return null;
  }

  const trimmedUrl = url.trim();

  if (!trimmedUrl) {
    return null;
  }

  if (isUrl(trimmedUrl)) {
    return null;
  }

  return trimmedUrl.includes("://")
    ? `Enter a full valid URL, for example https://example.com`
    : `This URL looks incomplete. Enter a full valid URL, for example https://example.com`;
}

function validatePostUrls(
  errors: Errors,
  post: Post,
  platformFields: PlatformFields,
  platforms: Platform[]
) {
  const postsToValidate = [
    post,
    ...(platforms || [])
      .filter((platform) => platformFields[platform.TYPE]?.unlinked)
      .map((platform) => ({ ...post, ...platformFields[platform.TYPE] })),
  ];

  for (const currentPost of postsToValidate) {
    const redirectUrlError = getUrlValidationMessage(currentPost.redirectUrl);

    if (redirectUrlError) {
      addFieldError(errors, FIELDS.URL, redirectUrlError);
    }

    const storyUrlError = getUrlValidationMessage(currentPost.storyUrl);

    if (storyUrlError) {
      addFieldError(errors, FIELDS.STORY_URL, storyUrlError);
    }
  }
}

export function getThreadsEffectiveCaptionLength(post: CaptionCountPost) {
  return getPlatformEffectiveCaptionLength({
    post,
    platformType: "TH",
    source: "threads",
  });
}

export function getLinkedInEffectiveCaptionLength(post: CaptionCountPost) {
  return getPlatformEffectiveCaptionLength({
    post,
    platformType: "LI",
    source: "linkedin",
    redirectUrlSeparator: " \n\n ",
  });
}

export function getTwitterEffectiveCaptionLength(post: {
  caption?: string | null;
  redirectUrl?: string | null;
}) {
  const caption = post.caption ?? "";
  const redirectUrl = post.redirectUrl?.trim();
  const fullCaption = `${caption}${redirectUrl ? ` ${redirectUrl}` : ""}`;

  return twitter.parseTweet(fullCaption).weightedLength;
}

function getTwitterLongformCapabilityState(
  accounts: Array<{
    platformType?: string | null;
    twData?: {
      subscription_type?: string | null;
    };
  }> = []
) {
  const twitterAccounts = accounts.filter(
    (account) => account.platformType === "TW"
  );

  return {
    allCapable:
      twitterAccounts.length > 0 &&
      twitterAccounts.every(
        (account) => getTwitterLongformCapability(account as any) === "capable"
      ),
    anyNotCapable: twitterAccounts.some(
      (account) =>
        getTwitterLongformCapability(account as any) === "not-capable"
    ),
    allNotCapable:
      twitterAccounts.length > 0 &&
      twitterAccounts.every(
        (account) =>
          getTwitterLongformCapability(account as any) === "not-capable"
      ),
  };
}

export function getTwitterPaidAccountWarning(
  post: {
    caption?: string | null;
    redirectUrl?: string | null;
  },
  accounts: Array<{
    platformType?: string | null;
    twData?: {
      subscription_type?: string | null;
    };
  }> = []
) {
  const { allCapable, anyNotCapable } =
    getTwitterLongformCapabilityState(accounts);
  const weightedLength = getTwitterEffectiveCaptionLength(post);

  if (
    weightedLength <= TWITTER_SHORT_LIMIT ||
    weightedLength > TWITTER_LONGFORM_LIMIT ||
    allCapable ||
    anyNotCapable
  ) {
    return null;
  }

  return `Posts over ${TWITTER_SHORT_LIMIT} characters require a paid X account. If any selected X account isn't a paid X account, this post will fail for that account.`;
}

export function getPostCaptionCharacterCount(
  post: CaptionCountPost,
  platforms: Array<{ TYPE: string }>,
  selectedPlatform: FormPlatformType,
  accounts: Array<{
    platformType?: string | null;
    twData?: {
      subscription_type?: string | null;
    };
  }> = []
) {
  const hasTwitter = platforms.some((platform) => platform.TYPE === "TW");
  const hasThreads = platforms.some((platform) => platform.TYPE === "TH");
  const { anyNotCapable: anyTwitterAccountsNotCapable } =
    getTwitterLongformCapabilityState(accounts);
  const twitterLimit = anyTwitterAccountsNotCapable
    ? TWITTER_SHORT_LIMIT
    : TWITTER_LONGFORM_LIMIT;

  if (selectedPlatform === "TW") {
    return getTwitterEffectiveCaptionLength(post);
  }

  if (selectedPlatform === "TH") {
    return getThreadsEffectiveCaptionLength(post);
  }

  if (selectedPlatform === "LI") {
    return getLinkedInEffectiveCaptionLength(post);
  }

  if (selectedPlatform === "LINKED") {
    const strictestLimit = Math.min(
      ...platforms.map((platform) =>
        platform.TYPE === "TW"
          ? twitterLimit
          : CHARACTER_LIMITS[platform.TYPE as keyof typeof CHARACTER_LIMITS] ||
            CHARACTER_LIMITS.FB
      )
    );

    if (hasTwitter && strictestLimit === twitterLimit) {
      return getTwitterEffectiveCaptionLength(post);
    }

    if (hasThreads && strictestLimit === CHARACTER_LIMITS.TH) {
      return getThreadsEffectiveCaptionLength(post);
    }

    if (
      platforms.some((platform) => platform.TYPE === "LI") &&
      strictestLimit === CHARACTER_LIMITS.LI
    ) {
      return getLinkedInEffectiveCaptionLength(post);
    }
  }

  return post.caption?.length ?? 0;
}

export const validate = (
  postState: string,
  post: Post,
  platformFields: PlatformFields,
  platforms: Platform[],
  overlapSchedule: IOverlapSchedule,
  _onValidateFull?: boolean,
  userTimezone: string = "",
  /**
   * How the server says this post would publish, per account. LAST and OPTIONAL
   * on purpose: absent means every phone-era limit still applies, so a caller
   * that has not been taught about tracks behaves exactly as it did.
   */
  igPreflight?: IgPreflightResult
): [Errors, Warnings] => {
  /*
      The form can have error states (currently defined)
      and warning states.

      An error state should mean that the form is not able to be
      saved. The user must fix the errors prior to saving.

      Warning states are used where the subsequent post will likely
      be different to what the user might think (e.g. it will not include
      a file for one social network). The user can still save the post
      as it will still go "live". Errors should be used where the post
      will not be posted at all.
   */
  // eslint-disable-next-line unicorn/prefer-set-has
  const platformTypes = map(platforms, "TYPE");
  const hasIG = platformTypes.includes("IG");
  const hasFB = platformTypes.includes("FB");
  const hasTW = platformTypes.includes("TW");
  const hasLI = platformTypes.includes("LI");
  const hasPI = platformTypes.includes("PI");
  const hasGMB = platformTypes.includes("GMB");
  const hasTH = platformTypes.includes("TH");
  const hasTT = platformTypes.includes("TT");
  /**
   * Format: {
   *   field: "",
   *   message: "",
   * }
   */
  const errors: Errors = {};
  const warnings: Warnings = {};
  // TEMPORARY PI ERROR
  if (post && post.accounts && post.accounts.length > 0) {
    const piAccounts = post.accounts.filter(filterAccountsByPlatformType("PI"));

    if (piAccounts && piAccounts.length > 1) {
      errors[FIELDS.ACCOUNTS] = {
        errors: [
          "You can currently only schedule a post to one Pinterest account at a time.",
        ],
      };
    }
  }

  const captionsToValidate = [
    post.caption,
    ...Object.values(platformFields || {})
      .filter((platformField) => platformField?.unlinked === true)
      .map((platformField) => get(platformField, FIELDS.CAPTION)),
  ];

  for (const caption of captionsToValidate) {
    const htmlLikeTagError = getHtmlLikeTagError(caption);
    if (htmlLikeTagError) {
      addFieldError(errors, FIELDS.CAPTION, htmlLikeTagError);
    }
  }

  const mediaFiles = extractMediaFiles(post);

  if (post && postState === POST_STATES.SCHEDULED) {
    if (!userTimezone) {
      errors[FIELDS.POST_STATE] = {
        errors: [
          "Unable to determine your timezone. Please refresh the page and try again.",
        ],
      };
    } else if (
      !post.isNow &&
      post.when &&
      isDateInThePast(
        post.when as string | Date | null | undefined,
        userTimezone
      )
    ) {
      errors[FIELDS.POST_STATE] = {
        errors: [`Scheduled date cannot be in the past (${userTimezone}).`],
      };
    }

    //post schedule overlap another one
    if (
      overlapSchedule &&
      overlapSchedule.overlappingPostId &&
      overlapSchedule.overlappingPostId !== post.id
    ) {
      const [, name] = getAccountNameParts({
        login: overlapSchedule.accountName || "",
      });
      const overlapAt = new Date(
        overlapSchedule.nextRunAt as string | number | Date
      );
      const overlapTime = format(overlapAt, DATE_FORMAT);
      const errorMessage =
        overlapSchedule.conflict === "sameMinute"
          ? `You already have a post scheduled for ${name} at ${format(
              overlapAt,
              "yyyy-MM-dd HH:mm"
            )}. Choose a different minute.`
          : `This ${
              post.isStory ? "story" : "post"
            } is scheduled within 15 minutes of a post already in the database at ${overlapTime} for ${name}. Please change the time.`;

      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      errors[FIELDS.POST_STATE]?.errors
        ? errors[FIELDS.POST_STATE].errors.push(errorMessage)
        : (errors[FIELDS.POST_STATE] = { errors: [errorMessage] });
    }
  }

  // Meta's Page Stories API takes no sticker, so a Facebook page that would
  // auto-publish cannot carry one. Publishing manually keeps it.
  if (hasFB && isAutoPublishedRegularStory(post) && post.storyUrl) {
    addFieldError(
      errors,
      FIELDS.STORY_URL,
      "Facebook stories cannot carry a link sticker. Remove the link, or publish this story manually."
    );
  }

  const mediaErrors: MediaFilesErrors = {};
  const mediaWarnings: MediaFilesWarning = {};

  const isAutoPublishedFacebookStory =
    hasFB && isAutoPublishedRegularStory(post);

  if (
    isAutoPublishedFacebookStory &&
    (!mediaFiles || mediaFiles.length === 0)
  ) {
    mediaErrors.FBStoryNoMedia = true;
  }

  if (mediaFiles && mediaFiles.length > 0) {
    const maxMediaFiles = isAutoPublishedFacebookStory ? API_STORY_SET_MAX : 10;
    if (mediaFiles.length > maxMediaFiles) {
      mediaErrors.tooManyFiles = maxMediaFiles;
    }

    /*
     * The three-item story-chain cap, SKE-4567.
     *
     * Three is a PHONE limit: a device publishes a story set as one message with
     * one outcome, so a long chain is a long time on a device that can fail part
     * way with nothing recorded. On the API track ONE job creates a container
     * per story and records each publish as it lands, so the cap protects
     * nothing and is lifted.
     *
     * `getStoryMediaLimit` is where that decision lives, shared with the
     * composer's add-media limit: it applies the cap only when an Instagram
     * account is selected, and keeps it when the server has not told us
     * otherwise.
     *
     * The NUMBER is the package's, SKE-4671, because the server now refuses an
     * over-cap save with the same constant. A literal here would let the
     * composer permit a chain the save path rejects.
     */
    if (
      mediaFiles.length > PHONE_STORY_CHAIN_MAX &&
      getStoryMediaLimit(post, platformTypes, igPreflight) ===
        PHONE_STORY_CHAIN_MAX
    ) {
      mediaErrors.IGStoryItemsCount = true;
    }

    /*
      Start ratio check for IG feed posts

      For Instagram feed posts (not stories), the 2nd and later
      assets should always be the same aspect ratio as the first
      item in the array.

      Do not run this for stories as it doesn't matter
     */
    if (hasIG && post.type !== POST_TYPES.STORY) {
      // ratio of the first file in the array
      const ratio = (() => {
        // eslint-disable-next-line unicorn/prefer-ternary
        const firstMediaMeta = mediaFiles[0].meta as
          ValidationMediaMeta | undefined;
        if (
          (mediaFiles[0] as PostMediaFile & { size?: number }).size ||
          firstMediaMeta
        ) {
          return getRatio(mediaFiles[0] as never);
        } else {
          // return 0 if we don't know/have meta
          return 0;
        }
      })();

      // check the ratios of later posts if we have a ratio for
      // the first post - they shoudl all be equal
      const ratioErrIndexes: number[] = [];
      if (ratio > 0) {
        mediaFiles.map((mediaFile, index) => {
          const mediaSize = (mediaFile as PostMediaFile & { size?: number })
            .size;
          if (mediaSize || mediaFile.meta) {
            if (getRatio(mediaFile as never) !== ratio) {
              ratioErrIndexes.push(index);
            }
          } else {
            // no metadata so we can't check ratio errors
          }
        });
      }

      if (ratioErrIndexes.length > 0) {
        mediaWarnings.notSameRatioIndexes = ratioErrIndexes;
      }
    }
    // End IG profile ratio check

    /* START MEDIAFILES VALIDATIONS */

    /*
      Platform resolution and aspect ratio checks

      Check resolution and aspect ratios of files
      Against the recommended minimums for each platform
     */
    const resolutionWarnIndexesIG: number[] = [];
    const resolutionErrIndexesFB: number[] = [];
    const resolutionWarnIndexesFB: number[] = [];
    const videoIndexes: number[] = [];
    const documentIndexes: number[] = [];
    const imageIndexes: number[] = [];

    // minimum pixels for FB
    if (post.type === POST_TYPES.STORY) {
      fbMinimum = 500;
    }
    for (const [index, mediaFile] of mediaFiles.entries()) {
      const mediaMeta = mediaFile.meta as ValidationMediaMeta | undefined;
      const mediaSize = (mediaFile as PostMediaFile & { size?: number }).size;
      if (mediaSize && !mediaMeta) {
        // shim
        mediaFile.meta = { size: mediaSize };
      }

      if (mediaFile.meta) {
        const meta = mediaFile.meta as ValidationMediaMeta;
        if ((meta.width ?? 0) < 1080) {
          resolutionWarnIndexesIG.push(index);
          resolutionWarnIndexesFB.push(index);
        }
        // FB requires width and height to be at least 120px
        if ((meta.width ?? 0) < fbMinimum || (meta.height ?? 0) < fbMinimum) {
          resolutionErrIndexesFB.push(index);
        }
      }

      if (isVideoMedia(mediaFile)) {
        videoIndexes.push(index);
      } else if (
        mediaFile.type === "document" ||
        (mediaFile.url && mediaFile.url.includes(".pdf"))
      ) {
        documentIndexes.push(index);
      } else {
        imageIndexes.push(index);
      }

      // IG FEED RATIO CHECK
      // Validate Instagram specifically here rather than platform config
      // as we need to do IG feed separate to IG story
      if (hasIG && post.type !== POST_TYPES.STORY) {
        // Check that videos are at least 3 seconds
        if (videoIndexes.length > 0 && !post[FIELDS.PUBLISH_STORY_MANUALLY]) {
          const duration = getMediaFileDuration(mediaFiles[index] as never);
          if (duration && duration < 3) {
            (mediaErrors.IGVideoTooShortVideoIndexes ??= []).push(index);
          }
        }
        if (
          getRatio(mediaFile as never) > 0 &&
          (getRatio(mediaFile as never) < 0.56 ||
            getRatio(mediaFile as never) > 1.91)
        ) {
          (mediaWarnings.IGRatioRecommendationIndexes ??= []).push(index);
        }
      }
      // IG STORY RATIO CHECK
      // Validate Instagram stories here rather than platform config
      // as we need to do IG feed separate to IG story
      // stories should be approx 0.6 (1836x3019), show warning otherwise
      if (hasIG && isAutoPublishedRegularStory(post)) {
        // check stories are 3-15 seconds
        // only run this validation if the story is NOT set to post manually.
        // user can crop manually or if it is a reels or IGTV post then they can post manually
        // we do not do any validations for items posted manually so do not worry about IGTV/Reels spec.
        if (videoIndexes.length > 0) {
          const duration = getMediaFileDuration(mediaFiles[index] as never);
          if (duration) {
            if (duration < 3) {
              (mediaErrors.IGStoryVideoTooShortVideoIndexes ??= []).push(index);
            } else if (duration > 60) {
              (mediaErrors.IGStoryVideoTooLongVideoIndexes ??= []).push(index);
            }
          }
        }
        const currentRatio = getRatio(mediaFile as never);
        // exclude if 1080x1920 - these stories are now supported
        // for auto story publishing
        if (currentRatio > 0 && (currentRatio < 0.56 || currentRatio > 0.61)) {
          (mediaWarnings.IGStoryRatioIndexes ??= []).push(index);
        }
      }
    }
    if (hasIG && resolutionWarnIndexesIG.length > 0) {
      mediaWarnings.IGResolutionIndexes = resolutionWarnIndexesIG;
    }
    if (
      hasFB && // TODO: check it. resolutionWarnIndexesFB contain indexes for mediaFile.meta.width < 1080
      // but we text "Facebook's recommended 500 pixels"

      resolutionErrIndexesFB &&
      resolutionErrIndexesFB.length > 1
    ) {
      mediaWarnings.FBResolutionIndexes = resolutionErrIndexesFB;
    }

    // END Platform resolution and aspect ratio checks

    // START VIDEO FILE CHECKS
    /* START FB VALIDATIONS */
    // FB validations - does not support videos as part of a carousel, so warn if mediaFiles > 1
    // (we will post only the first one(s)
    if (hasFB) {
      if (videoIndexes.length > 0) {
        for (const index of videoIndexes) {
          const duration = getMediaFileDuration(mediaFiles[index] as never);
          if (duration) {
            if (duration < 1) {
              (mediaErrors.FBVideoTooShortVideoIndexes ??= []).push(index);
            } else if (duration > 14_400) {
              (mediaErrors.FBVideoTooLongVideoIndexes ??= []).push(index);
            }
          }
        }
      }
      // A story set publishes each item as its own story, videos included.
      if (
        videoIndexes.length > 0 &&
        mediaFiles.length > 1 &&
        post.type !== POST_TYPES.STORY
      ) {
        mediaWarnings.FBVideoIndexes = videoIndexes;
      }
      // Manual stories, reels and IGTV go out by notification, unchecked.
      if (isAutoPublishedRegularStory(post)) {
        validateFacebookStoryMedia({
          mediaFiles,
          videoIndexes,
          imageIndexes,
          mediaErrors,
          mediaWarnings,
        });
      }
    }
    /* END FB VALIDATIONS */

    /* START LI VALIDATIONS */
    // LinkedIn validations - does not support videos in carousels
    if (hasLI) {
      validateLinkedIn({
        mediaErrors,
        mediaFiles,
        post,
        videoIndexes,
        imageIndexes,
        mediaWarnings,
      });
    }

    /* END LI VALIDATIONS */
    if (hasTH) {
      // no docs on TH
      if (post.type === POST_TYPES.DOCUMENT || documentIndexes.length > 0) {
        mediaWarnings.THDocumentNotSupported = true;
      }
      // images are auto scaled by threads platform
      if (imageIndexes.length > 0) {
        for (const index of imageIndexes) {
          const meta = mediaFiles[index].meta as
            ValidationMediaMeta | undefined;
          if (!meta) {
            continue;
          }
          if ((meta.width ?? 0) > 1440 || (meta.width ?? 0) < 360) {
            (mediaWarnings.THResolutionImageIndexes ??= []).push(index);
          }
          if (Number.isInteger(meta.size) && (meta.size ?? 0) > 8_000_000) {
            (mediaErrors.THImageTooBigIndexes ??= []).push(index);
          }
        }
      }
      // check video attributes
      if (videoIndexes.length > 0) {
        for (const index of videoIndexes) {
          const meta = mediaFiles[index].meta as
            ValidationMediaMeta | undefined;
          if (!meta) {
            continue;
          }
          const duration = getMediaFileDuration(mediaFiles[index] as never);
          if (duration) {
            if (duration < 1) {
              (mediaErrors.THVideoTooShortIndexes ??= []).push(index);
            } else if (duration > 300) {
              (mediaErrors.THVideoTooLongIndexes ??= []).push(index);
            }
          }
          // some videos have "N/A" for size but just in case
          // they get a value, we will check for it
          if (Number.isInteger(meta.size) && (meta.size ?? 0) > 10_000_000) {
            (mediaErrors.THVideoTooBigIndexes ??= []).push(index);
          }
          if ((meta.width ?? 0) > 1920) {
            (mediaErrors.THVideoTooWideIndexes ??= []).push(index);
          }
        }
      }
    }

    /* START PI VALIDATIONS */
    if (hasPI) {
      if (mediaFiles.length > 1 && videoIndexes.length > 0) {
        mediaErrors.PIVideosInCarouselIndexes = videoIndexes;
      }

      if (imageIndexes.length > 5) {
        mediaErrors.PIImagesOverLimit = true;
      }
    }
    /* END PI VALIDATIONS */

    /* START TW VALIDATIONS */
    // Twitter validations - videos not supported in carousel, only images. So fail if mediaFiles > 1
    if (hasTW) {
      // at least two mediaFile in the array and 1+ video
      if (mediaFiles.length > 1 && videoIndexes.length > 0) {
        if (imageIndexes.length === 0) {
          // all of the mediaFiles are videos, so we cannot post this post
          mediaErrors.TWImagesLimit = true;
        } else {
          // hast at least one image
          mediaWarnings.TWHasVideoInCarousel = true;
        }
      }
      if (videoIndexes.length > 0) {
        videoIndexes.map((index) => {
          const duration = getMediaFileDuration(mediaFiles[index] as never);
          if (duration) {
            if (duration < 1) {
              (mediaErrors.TWVideoTooShortIndexes ??= []).push(index);
            } else if (duration > 140) {
              (mediaErrors.TWVideoTooLongIndexes ??= []).push(index);
            }
          }
        });
      }
      // where mediaFiles is 5+ items and there is at least one image and one video, warn that we will only post the images
      if (
        mediaFiles.length > 4 &&
        imageIndexes.length > 0 &&
        videoIndexes.length > 0
      ) {
        mediaWarnings.TWFilesLimit = true;
      }

      if (mediaFiles.length > 4) {
        if (postState === POST_STATES.DRAFT) {
          mediaWarnings.TWFilesLimit = true;
        } else {
          mediaErrors.TWImagesOverLimit = true;
        }
      }
    }
    /* END TW VALIDATIONS */

    /* START GMB VALIDATIONS */
    // GMB validations - will always not post videos
    if (hasGMB) {
      // videos not supported
      const gmbPost = {
        ...post,
        ...platformFields.GMB,
      } as GoogleMyBusinessPost;
      const firstGmbImageIndex = mediaFiles.findIndex(
        (mediaFile) =>
          !mediaFile.isVideo &&
          mediaFile.type !== "document" &&
          !(mediaFile.url && mediaFile.url.includes(".pdf"))
      );
      const firstGmbImage =
        firstGmbImageIndex >= 0 ? mediaFiles[firstGmbImageIndex] : null;

      if (videoIndexes.length > 0) {
        if (imageIndexes.length > 0) {
          mediaWarnings.GMBHasVideo = true;
        } else {
          // only videos so error this post
          mediaErrors.GMBHasVideo = true;
        }
      }

      // Will only post the first image of a  carousel
      if (mediaFiles.length > 1 && imageIndexes.length > 0) {
        mediaWarnings.GMBHasMoreOneImages = true;
      }
      if (
        firstGmbImage &&
        firstGmbImage.meta &&
        (((firstGmbImage.meta as ValidationMediaMeta).width ?? 0) < 250 ||
          ((firstGmbImage.meta as ValidationMediaMeta).height ?? 0) < 250)
      ) {
        mediaErrors.GMBResolutionIndexes = [firstGmbImageIndex];
      }
      if (
        firstGmbImage &&
        typeof (firstGmbImage.meta as ValidationMediaMeta | undefined)
          ?.byteSize === "number" &&
        ((firstGmbImage.meta as ValidationMediaMeta).byteSize ?? 0) <
          GMB_MIN_IMAGE_BYTES
      ) {
        mediaErrors.GMBFileSizeIndexes = [firstGmbImageIndex];
      }
      // ensure we have a topic type
      if (!gmbPost.gmbTopicType) {
        errors[FIELDS.GMB_TOPIC_TYPE] = {
          errors: ["A topic type is required."],
        };
      }
    }
    /* END GMB VALIDATIONS */

    if (hasTT) {
      validateTTMedia({
        post,
        mediaErrors,
        mediaWarnings,
        mediaFiles,
        imageIndexes,
        videoIndexes,
      });
    }

    // Video/Reel validation for FB
    if ((hasIG || hasFB) && post.videoPostType) {
      const accounts = post.accounts || [];
      const criteria = getVideoReelCriteria(mediaFiles, accounts);

      if (post.videoPostType === "reel" && !shouldShowReelToggle(criteria)) {
        mediaWarnings.videoReelNotSupported = true;
      }

      if (
        post.videoPostType === "reel" &&
        criteria.hasVideo &&
        criteria.isNineToSixteen &&
        !criteria.isShortDuration
      ) {
        mediaWarnings.reelTooLong = true;
      }
    }
  }
  /* END MEDIAFILES VALIDATIONS */

  if (hasIG) {
    const notices = preflightNotices(igPreflight);

    if (notices.mediaErrors.length > 0) {
      mediaErrors.IGPreflightBlockers = notices.mediaErrors;
    }
    if (notices.mediaWarnings.length > 0) {
      mediaWarnings.IGPreflightWarnings = notices.mediaWarnings;
    }
    if (notices.formErrors.length > 0) {
      errors[FIELDS.IG_PREFLIGHT] = { errors: notices.formErrors };
    }
    if (notices.formWarnings.length > 0) {
      warnings[FIELDS.IG_PREFLIGHT] = { warnings: notices.formWarnings };
    }
  }

  if (Object.keys(mediaErrors).length > 0) {
    errors[FIELDS.MEDIA] = {
      errors: mediaErrors,
    };
  }

  if (Object.keys(mediaWarnings).length > 0) {
    warnings[FIELDS.MEDIA] = {
      warnings: mediaWarnings,
    };
  }

  const validatePlatform = (platform: Platform, post: Post) => {
    const rules = postPlatformsConfig[platform.TYPE];
    if (!rules) {
      return;
    }
    const validation = rules.validation as any;
    if (!validation) {
      return;
    }

    // check errors
    if (validation.error && Object.keys(validation.error).length > 0) {
      Object.keys(validation.error).map((field) => {
        // do not test type in light validation
        if (field !== FIELDS.TYPE) {
          Object.keys(validation.error[field]).map((ruleKey) => {
            const value = getValueForRule({
              field: field as PostFormFieldName,
              ruleKey: ruleKey as RulesType,
              platform,
              post,
            });
            const errMsg = validateRule(
              field,
              ruleKey as RulesType,
              validation.error[field][ruleKey],
              value,
              platform,
              mediaFiles
            );
            if (errMsg !== null && field !== FIELDS.MEDIA) {
              if (!errors[field]) {
                errors[field] = { errors: [] };
              }
              errors[field]["errors"].push(errMsg);
            }

            if (errMsg !== null && field === FIELDS.MEDIA) {
              if (!errors[field]) {
                errors[field] = { errors: {} };
              }
              (errors[field]["errors"] as Record<string, unknown>)[
                "tooManyFiles"
              ] = validation.error[field][ruleKey][1];
            }
          });
        }
      });
    }
    // check warnings
    if (validation.warn && Object.keys(validation.warn).length > 0) {
      Object.keys(validation.warn).map((field) => {
        Object.keys(validation.warn[field]).map((ruleKey) => {
          const errMsg = validateRule(
            field,
            ruleKey as RulesType,
            validation.warn[field][ruleKey],
            getValueForRule({
              field: field as PostFormFieldName,
              ruleKey: ruleKey as RulesType,
              platform,
              post,
            }),
            platform,
            mediaFiles,
            undefined,
            "warning"
          );
          if (errMsg !== null) {
            if (!warnings[field]) {
              warnings[field] = { warnings: [] };
            }
            warnings[field]["warnings"].push(errMsg);
          }
        });
      });
    }
  };

  const linkedPlatforms = platforms.filter(
    (platform) => !platformFields[platform.TYPE]?.unlinked
  );

  const unlinkedPlatforms = Object.keys(platformFields)
    /**TODO: competely wrong logic as key does not have unlinked
     * left it here as it has been like this for a very long time
     */
    .filter(
      (platform) => platformFields[platform as FormPlatformType]?.unlinked
    )
    .map(
      (platformKey) =>
        platforms.find((platform) => platform.TYPE === platformKey) ??
        PLATFORMS[platformKey as keyof typeof PLATFORMS]
    )
    .filter((platform): platform is Platform => Boolean(platform));

  // Process rules for each platform.
  for (const platform of linkedPlatforms) validatePlatform(platform, post);
  for (const platform of unlinkedPlatforms)
    validatePlatform(platform, {
      ...post,
      ...platformFields[platform.TYPE as keyof PlatformFields],
    });

  validatePostUrls(errors, post, platformFields, platforms);

  return [errors, warnings];
};

const isOffNineToSixteen = (mediaFile: Post["mediaFiles"][number]) =>
  getRatio(mediaFile as never) > 0 && !isNineToSixteenRatio(mediaFile as never);

/**
 * Facebook story media on auto-publish, IDEA-622. Meta's Page Stories API
 * refuses these outright, so they block the save rather than fail at publish.
 * The one warning is a photo outside 9:16, which Meta accepts and letterboxes.
 */
function validateFacebookStoryMedia({
  mediaFiles,
  videoIndexes,
  imageIndexes,
  mediaErrors,
  mediaWarnings,
}: {
  mediaFiles: Post["mediaFiles"];
  videoIndexes: number[];
  imageIndexes: number[];
  mediaErrors: MediaFilesErrors;
  mediaWarnings: MediaFilesWarning;
}) {
  for (const index of videoIndexes) {
    const mediaFile = mediaFiles[index];
    const duration = getMediaFileDuration(mediaFile as never);
    if (duration) {
      if (duration < FACEBOOK_STORY_CONSTANTS.DURATION.MIN_SECONDS) {
        (mediaErrors.FBStoryVideoTooShortIndexes ??= []).push(index);
      } else if (duration > FACEBOOK_STORY_CONSTANTS.DURATION.MAX_SECONDS) {
        (mediaErrors.FBStoryVideoTooLongIndexes ??= []).push(index);
      }
    }

    const meta = mediaFile.meta as ValidationMediaMeta | undefined;
    if (meta?.width && meta?.height) {
      if (
        meta.width < FACEBOOK_STORY_CONSTANTS.MIN_VIDEO_WIDTH ||
        meta.height < FACEBOOK_STORY_CONSTANTS.MIN_VIDEO_HEIGHT
      ) {
        (mediaErrors.FBStoryVideoResolutionIndexes ??= []).push(index);
      }
    }

    if (isOffNineToSixteen(mediaFile)) {
      (mediaErrors.FBStoryVideoRatioIndexes ??= []).push(index);
    }
  }

  for (const index of imageIndexes) {
    const mediaFile = mediaFiles[index];
    const meta = mediaFile.meta as ValidationMediaMeta | undefined;
    const bytes = meta?.byteSize ?? meta?.size;
    if (
      Number.isInteger(bytes) &&
      (bytes ?? 0) > FACEBOOK_STORY_CONSTANTS.MAX_PHOTO_BYTES
    ) {
      (mediaErrors.FBStoryPhotoTooBigIndexes ??= []).push(index);
    }

    const extension = getFileExtension(
      mediaFile.fileName || mediaFile.url || ""
    );
    if (
      extension &&
      !(
        FACEBOOK_STORY_CONSTANTS.PHOTO_EXTENSIONS as readonly string[]
      ).includes(extension)
    ) {
      (mediaErrors.FBStoryPhotoFormatIndexes ??= []).push(index);
    }

    if (isOffNineToSixteen(mediaFile)) {
      (mediaWarnings.FBStoryPhotoRatioIndexes ??= []).push(index);
    }
  }
}

function validateTTMedia({
  mediaErrors,
  videoIndexes,
  imageIndexes,
  mediaFiles,
  mediaWarnings,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post: any;
  mediaErrors: MediaFilesErrors;
  mediaWarnings: MediaFilesWarning;
  mediaFiles: Post["mediaFiles"];
  imageIndexes: number[];
  videoIndexes: number[];
}) {
  if (videoIndexes.length > 1) {
    mediaErrors.TTVideoTooMany = true;
    return;
  }
  if (videoIndexes.length > 0 && imageIndexes.length > 0) {
    mediaErrors.TTMixedPhotoVideo = true;
  }

  if (
    mediaFiles.some(
      (c) => getMediaType(c) !== "image" && getMediaType(c) !== "video"
    )
  ) {
    mediaWarnings.TTUnsupportedMediaFile = true;
  }
}

export const validateFull = (
  postState: PostStatesType,
  post: Post,
  platformFields: PlatformFields,
  platforms: Platform[],
  overlapSchedule: IOverlapSchedule,
  userTimezone: string = "",
  igPreflight?: IgPreflightResult
) => {
  let [errors, warnings] = validate(
    postState,
    post,
    platformFields,
    platforms,
    overlapSchedule,
    true,
    userTimezone,
    igPreflight
  );
  // validate that the selected accounts have queue enabled
  if (
    postState === POST_STATES.QUEUED &&
    post.accounts &&
    post.accounts.length > 0
  ) {
    post.accounts.map((account) => {
      if (!account.queueEnabled) {
        if (!errors[FIELDS.POST_STATE]) {
          errors[FIELDS.POST_STATE] = { errors: [] };
        }
        // show one error box per account that does not have queue enabled
        // so the customer knows which to remove or change
        errors[FIELDS.POST_STATE]["errors"].push(
          `${account.loginAlias || account.login} (${
            account.platformType || "IG"
          }) does not have queue enabled. To schedule this post to ${
            account.loginAlias || account.login
          }, pick a scheduled time instead or set up the queue in queue settings.`
        );
      }
    });
  }
  // check GMB
  const [gmbErrors, gmbWarnings] = validateGmb(
    postState,
    post,
    platformFields,
    platforms
  );

  if (gmbErrors) {
    errors = { ...errors, ...gmbErrors };
  }
  if (gmbWarnings) {
    warnings = { ...warnings, ...gmbWarnings };
  }

  // check PI
  const [piErrors, piWarnings] = validatePi(
    postState,
    post,
    platformFields,
    platforms
  );

  if (piErrors) {
    errors = { ...errors, ...piErrors };
  }
  if (piWarnings) {
    warnings = { ...warnings, ...piWarnings };
  }
  // validate twitter with the relevant library
  const [twErrors, twWarnings] = validateTw(
    postState,
    post,
    platformFields,
    platforms
  );

  if (twErrors) {
    errors = { ...errors, ...twErrors };
  }
  if (twWarnings) {
    warnings = { ...warnings, ...twWarnings };
  }
  const [liErrors, liWarnings] = validateLi(
    postState,
    post,
    platformFields,
    platforms
  );

  if (liErrors) {
    errors = { ...errors, ...liErrors };
  }
  if (liWarnings) {
    warnings = { ...warnings, ...liWarnings };
  }
  // validate threads with the relevant library
  const [thErrors, thWarnings] = validateTh(
    postState,
    post,
    platformFields,
    platforms
  );

  if (thErrors) {
    errors = { ...errors, ...thErrors };
  }
  if (thWarnings) {
    warnings = { ...warnings, ...thWarnings };
  }
  // Process rules for each platform.

  function validatePlatform(
    platform: Platform,
    post: Post,
    onValidateFull?: boolean
  ) {
    const rules = postPlatformsConfig[platform.TYPE];
    if (!rules) {
      return;
    }
    const validation = rules.validation as any;
    if (!validation) {
      return;
    }
    const mediaFiles = extractMediaFiles(post);
    // check errors only
    if (validation.error && Object.keys(validation.error).length > 0) {
      Object.keys(validation.error).map((field) => {
        // do not test type in light validation
        Object.keys(validation.error[field]).map((ruleKey) => {
          const errMsg = validateRule(
            field,
            ruleKey as RulesType,
            validation.error[field][ruleKey],
            getValueForRule({
              field: field as PostFormFieldName,
              ruleKey: ruleKey as RulesType,
              platform,
              post,
            }),
            platform,
            mediaFiles,
            onValidateFull
          );
          if (errMsg !== null) {
            if (!errors[field]) {
              errors[field] = { errors: [] };
            }
            errors[field]["errors"].push(errMsg);
          }
        });
      });
    }

    if (validation.warn && Object.keys(validation.warn).length > 0) {
      Object.keys(validation.warn).map((field) => {
        Object.keys(validation.warn[field]).map((ruleKey) => {
          const warningMsg = validateRule(
            field,
            ruleKey as RulesType,
            validation.warn[field][ruleKey],
            getValueForRule({
              field: field as PostFormFieldName,
              ruleKey: ruleKey as RulesType,
              platform,
              post,
            }),
            platform,
            mediaFiles,
            onValidateFull,
            "warning"
          );

          if (warningMsg !== null) {
            if (!warnings[field]) {
              warnings[field] = { warnings: [] };
            }

            warnings[field]["warnings"].push(warningMsg);
          }
        });
      });
    }
  }

  const { linkedPlatforms, unlinkedPlatforms }: LinkedUnlinkedPlatforms =
    platforms.reduce(
      (acum: LinkedUnlinkedPlatforms, platform: Platform) => {
        if (platformFields[platform.TYPE]?.unlinked) {
          acum.unlinkedPlatforms.push(platform);
        } else {
          acum.linkedPlatforms.push(platform);
        }

        return acum;
      },
      { linkedPlatforms: [], unlinkedPlatforms: [] }
    );

  // Process rules for each platform.
  for (const platform of linkedPlatforms)
    validatePlatform(platform, post, true);
  for (const platform of unlinkedPlatforms)
    validatePlatform(
      platform,
      { ...post, ...platformFields[platform.TYPE] },
      true
    );

  // validate empty posts
  const isEmpty = !(post.caption || post.url || post.mediaFiles?.length);
  const topicType =
    (post as Post & { gmbTopicType?: string }).gmbTopicType || null;
  const isGMBAlertPost = topicType === GMB_TOPICS.ALERT.VALUE;

  if (isEmpty && !isGMBAlertPost) {
    const action =
      postState === POST_STATES.SCHEDULED || postState === POST_STATES.QUEUED
        ? "schedule"
        : "save";

    errors[FIELDS.POST_STATE] = {
      errors: [
        `You can't ${action} empty post, at least 1 attachment or caption is required.`,
      ],
    };
  }

  validatePostUrls(errors, post, platformFields, platforms);

  return [errors, warnings];
};
const validateTh = (
  _postState: PostStatesType,
  post: Post,
  platformFields: PlatformFields,
  platforms: Platform[]
): [Errors?, Warnings?] => {
  const errors: Errors = {};
  const warnings: Warnings = {};
  const isThPost = platforms.some((platform) => platform.TYPE === "TH");
  if (!platforms) {
    return [];
  }

  if (!isThPost) {
    return [];
  }

  const fullPost = { ...post, ...platformFields.TH };

  const result = getThreadsEffectiveCaptionLength(fullPost);
  if (result > 500) {
    errors[FIELDS.CAPTION] = {
      errors: [
        `Your Threads caption (including any URL, and UTMs if added automatically) is over the 500 character limit. It is ${result} characters long.`,
      ],
    };
  }

  return [errors, warnings];
};
const validateTw = (
  _postState: PostStatesType,
  post: Post,
  platformFields: PlatformFields,
  platforms: Platform[]
): [Errors?, Warnings?] => {
  const errors: Errors = {};
  const warnings: Warnings = {};
  const isTwPost = platforms.some((platform) => platform.TYPE === "TW");
  if (!platforms) {
    return [];
  }

  if (!isTwPost) {
    return [];
  }

  const fullPost = { ...post, ...platformFields.TW };

  const result = {
    weightedLength: getTwitterEffectiveCaptionLength(fullPost),
  };

  const warningMessage = getTwitterPaidAccountWarning(
    fullPost,
    post.accounts || []
  );
  const { anyNotCapable } = getTwitterLongformCapabilityState(
    post.accounts || []
  );

  if (result.weightedLength > TWITTER_LONGFORM_LIMIT) {
    // Absolute X ceiling: no account can post this, regardless of plan.
    errors[FIELDS.CAPTION] = {
      errors: [
        `Your Tweet (and URL if included) is over the maximum limit of ${TWITTER_LONGFORM_LIMIT} characters - it is ${result.weightedLength} characters long. We validate the full Tweet length including the URL only when you hit save.`,
      ],
    };
  } else if (result.weightedLength > TWITTER_SHORT_LIMIT) {
    if (anyNotCapable) {
      errors[FIELDS.CAPTION] = {
        errors: [
          `Posts over ${TWITTER_SHORT_LIMIT} characters require every selected X account to be on a Premium subscription. At least one selected X account is limited to ${TWITTER_SHORT_LIMIT} characters, so this post will fail unless you shorten it or edit account-specific captions.`,
        ],
      };
    } else if (warningMessage) {
      warnings[FIELDS.CAPTION] = {
        warnings: [warningMessage],
      };
    }
  }
  // result.valid being false at <=280 with weightedLength 0 is the
  // image/video-only case (no caption) — intentionally ignored, as before.

  return [errors, warnings];
};

const validateLi = (
  _postState: PostStatesType,
  post: Post,
  platformFields: PlatformFields,
  platforms: Platform[]
): [Errors?, Warnings?] => {
  const errors: Errors = {};
  const warnings: Warnings = {};
  const isLiPost = platforms.some((platform) => platform.TYPE === "LI");

  if (!platforms || !isLiPost) {
    return [];
  }

  const fullPost = { ...post, ...platformFields.LI };
  const result = getLinkedInEffectiveCaptionLength(fullPost);

  if (result > CHARACTER_LIMITS.LI) {
    errors[FIELDS.CAPTION] = {
      errors: [
        `Your LinkedIn caption (including any URL, and UTMs if added automatically) is over the ${CHARACTER_LIMITS.LI} character limit after tracking parameters are applied. It is ${result} characters long.`,
      ],
    };
  }

  return [errors, warnings];
};

const validateGmb = (
  _postState: PostStatesType,
  post: Post,
  platformFields: PlatformFields,
  platforms: Platform[]
): [Errors?, Warnings?] => {
  const errors: Errors = {};
  const warnings: Warnings = {};
  let isGMBPost = false;
  if (!platforms) {
    return [];
  }
  platforms.map((platform) => {
    if (platform.TYPE === "GMB") {
      isGMBPost = true;
    }
  });

  if (!isGMBPost) {
    return [];
  }

  const { gmbTopicType, gmbEvent, gmbCallToAction, redirectUrl } =
    mergeGmbPostFields(post, platformFields.GMB) as Post &
      GoogleMyBusinessPost & { redirectUrl?: string | null };

  if (!gmbTopicType) {
    errors[FIELDS.GMB_TOPIC_TYPE] = { errors: ["A topic type is required."] };
  }

  if (
    typeof gmbTopicType === "string" &&
    [GMB_TOPICS.OFFER.VALUE, GMB_TOPICS.EVENT.VALUE].includes(gmbTopicType)
  ) {
    if (!gmbEvent?.title) {
      errors[FIELDS.GMB_TITLE] = {
        errors: ["An event title is required."],
      };
    }
    if (gmbEvent?.title && gmbEvent.title.length > 58) {
      errors[FIELDS.GMB_TITLE] = {
        errors: ["Your event title can't be longer than 58 characters."],
      };
    }

    if (!gmbEvent?.startDate) {
      errors[FIELDS.GMB_START_DATE] = {
        errors: ["The event start date is required."],
      };
    }

    if (!gmbEvent || !gmbEvent.endDate) {
      errors[FIELDS.GMB_END_DATE] = {
        errors: ["The event end date is required."],
      };
    }

    if (gmbEvent?.startDate && gmbEvent?.endDate) {
      const startDate = new Date(gmbEvent.startDate);
      const endDate = new Date(gmbEvent.endDate);
      const now = new Date();

      if (isBefore(endDate, startDate)) {
        errors[FIELDS.GMB_END_DATE] = {
          errors: ["End date cannot be before start date."],
        };
      } else if (isBefore(startDate, now) && isBefore(endDate, now)) {
        errors[FIELDS.GMB_START_DATE] = {
          errors: ["Start and end dates are in the past."],
        };
      }
    }
  }

  // Alerts cannot have media
  if (
    gmbTopicType === GMB_TOPICS.ALERT.VALUE &&
    post.type !== POST_TYPES.TEXT
  ) {
    errors[FIELDS.GMB_TOPIC_TYPE] = {
      errors: ["Alert posts cannot have images or videos."],
    };
  }

  if (
    [
      GMB_TOPICS.STANDARD.VALUE,
      GMB_TOPICS.EVENT.VALUE,
      GMB_TOPICS.ALERT.VALUE,
    ].includes(gmbTopicType as string) &&
    gmbCallToAction &&
    "actionType" in gmbCallToAction &&
    gmbCallToAction.actionType &&
    isEmpty(redirectUrl) && // "URL is not needed for CALL actions"
    gmbCallToAction.actionType !== "CALL"
  ) {
    errors[FIELDS.GMB_CTA] = {
      errors: ["A URL is required for GMB posts with a call to action."],
    };
    errors[FIELDS.URL] = {
      errors: ["A URL is required for GMB posts with a call to action."],
    };
  }

  return [errors, warnings];
};

// Very simple validation at present - just ensure board is selected
const validatePi = (
  _postState: PostStatesType,
  _post: Post,
  _platformFields: PlatformFields,
  platforms: Platform[]
): [Errors?, Warnings?] => {
  const errors: Errors = {};
  const warnings: Warnings = {};
  let isPIPost = false;
  if (!platforms) {
    return [];
  }
  platforms.map((platform) => {
    if (platform.TYPE === "PI") {
      isPIPost = true;
    }
  });

  if (!isPIPost) {
    return [];
  }

  return [errors, warnings];
};

const validateRule = (
  field: string,
  rule: RulesType,
  ruleParams: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any,
  platform: Platform,
  mediaFiles: Post["mediaFiles"],
  onValidateFull?: boolean,
  severity: "error" | "warning" = "error"
) => {
  let errMsg = null;
  switch (rule) {
    case RULES.LENGTH: {
      const [min, max, customMsg] = ruleParams;

      if (value && value.length < min) {
        errMsg =
          customMsg ||
          `${platform.LABEL}: ${field} needs to be at least ${min} characters.`;
        break;
      }

      if (value && value.length > max) {
        errMsg =
          customMsg ||
          `${platform.LABEL}: ${field} - ${max} characters are supported.`;
        break;
      }
      break;
    }
    case RULES.HASHTAG_COUNT: {
      const [hashMin, hashMax] = ruleParams;
      const hashtagCount = countCharacter(value, "#");
      const requirementVerb = severity === "warning" ? "should" : "must";
      if (hashtagCount < hashMin) {
        errMsg = `${platform.LABEL}: Number of hashtags ${requirementVerb} not be less than ${hashMin} in both the caption and comment combined.`;
        break;
      }

      if (hashtagCount > hashMax) {
        errMsg = `${platform.LABEL}: Number of hashtags ${requirementVerb} not be greater than ${hashMax} in both the caption and comment combined.`;
        break;
      }
      break;
    }
    case RULES.HASHTAG_HARD_LIMIT: {
      const [, hashMax] = ruleParams;
      const hashtagCount = countCharacter(value, "#");
      if (hashtagCount > hashMax) {
        errMsg = `${platform.LABEL}: ${hashtagCount} hashtags in the caption. Instagram accepts at most ${hashMax} in a caption and rejects the post above that.`;
      }
      break;
    }
    case RULES.MENTION_COUNT: {
      const [hashMin, hashMax] = ruleParams;
      const mentionCount = countMentionsForPlatform({
        value,
        platformType: platform.TYPE,
      });
      if (mentionCount < hashMin) {
        errMsg = `${platform.LABEL}: Number of mentions must not be less than ${hashMin}`;
        break;
      }

      if (mentionCount > hashMax) {
        errMsg = `${platform.LABEL}: Number of mentions must not be greater than ${hashMax}`;
        break;
      }
      break;
    }
    case RULES.SUPPORTED_POST_TYPE: {
      const valueIn = ruleParams.includes(value);
      if (!valueIn) {
        errMsg = capitalize(
          `${platform.LABEL} does not support ${value} posts.`
        );
      } else if (
        value === POST_TYPES.MULTI_MEDIA &&
        mediaFiles?.some((c) => isDocumentMedia(c))
      ) {
        errMsg = capitalize(
          `${platform.LABEL} does not support document posts.`
        );
      }
      break;
    }
    case RULES.REQUIRED: {
      if (
        !value &&
        (!ruleParams?.onValidateFull ||
          (!!onValidateFull && !!ruleParams?.onValidateFull))
      ) {
        errMsg = ruleParams?.message || `${field} is required.`;
      }
      break;
    }
  }

  return errMsg;
};

export const getMediaType = (
  mediaFile: MediaUrlInfo | Post["mediaFiles"][number]
) => {
  if (isVideoMedia(mediaFile)) {
    return "video";
  } else if (
    mediaFile.type === "document" ||
    (mediaFile.url && mediaFile.url.includes(".pdf"))
  ) {
    return "document";
  }
  return "image";
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getSafeFieldValue(object: any, field: string | symbol | number) {
  if (String(field).includes(".")) {
    return get(object, field);
  }
  return object[field];
}

// given a platform type, return errors
// error wrapper will find the errors for platform, if "LINKED", then just take the first error for a field
// given a field return errors (only ever print out the first error for a field)

type Errors = FormValidationErrors;
type Warnings = FormValidationWarnings;

function normalizeMediaMetaForValidation(meta: unknown): ValidationMediaMeta {
  if (!meta || typeof meta !== "object" || Array.isArray(meta)) {
    return {};
  }

  const mediaMeta = meta as ValidationMediaMeta;

  if (typeof mediaMeta.byteSize === "number") {
    return mediaMeta;
  }

  return typeof mediaMeta.length === "number"
    ? {
        ...mediaMeta,
        byteSize: mediaMeta.length,
      }
    : mediaMeta;
}

function extractMediaFiles(post: Post): PostMediaFile[] {
  let mediaFiles: PostMediaFile[] = post.mediaFiles
    ? post.mediaFiles.map((mediaFile): PostMediaFile => {
        const { fileName, ...mediaFileFields } = mediaFile;

        return {
          ...mediaFileFields,
          ...(fileName === null || fileName === undefined ? {} : { fileName }),
          meta: normalizeMediaMetaForValidation(
            mediaFile.meta
          ) as PostMediaFile["meta"],
        } as PostMediaFile;
      })
    : [];

  // shim a mediaFile object in here so that we still run the validations
  if (mediaFiles && mediaFiles.length === 0 && post.url) {
    mediaFiles = [
      {
        altText: post.altText,
        url: post.url,
        thumbnailUrl: post.thumbnailUrl || "",
        meta: normalizeMediaMetaForValidation(post.meta),
        fileName: post.fileName || undefined,
        isVideo: post.isVideo || false,
        isDocument: post.isDocument || false,
      },
    ];
  }
  return mediaFiles;
}
